/**
 * Input Validation and Sanitization Utilities
 * Provides validation for business profile fields
 */

/**
 * Sanitize text input to prevent XSS
 * Removes HTML tags and dangerous characters
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';

  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validate and format phone number
 * Supports international formats
 * Returns { isValid: boolean, formatted: string, error: string }
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) {
    return { isValid: true, formatted: '', error: '' }; // Optional field
  }

  const sanitized = sanitizeText(phone);

  // Remove all non-digit characters except + at start
  const digitsOnly = sanitized.replace(/[^\d+]/g, '');

  // Check if starts with + (international format)
  const isInternational = digitsOnly.startsWith('+');

  // Extract only digits for length check
  const numbersOnly = digitsOnly.replace(/\+/g, '');

  // Validate length (7-15 digits for international numbers)
  if (numbersOnly.length < 7 || numbersOnly.length > 15) {
    return {
      isValid: false,
      formatted: sanitized,
      error: 'Phone number must be between 7 and 15 digits'
    };
  }

  // Format the number for display
  let formatted = digitsOnly;

  // US/Canada format: +1 (XXX) XXX-XXXX
  if (isInternational && digitsOnly.startsWith('+1') && numbersOnly.length === 11) {
    const match = digitsOnly.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      formatted = `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    }
  }
  // US/Canada without country code: (XXX) XXX-XXXX
  else if (!isInternational && numbersOnly.length === 10) {
    const match = digitsOnly.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      formatted = `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }

  return {
    isValid: true,
    formatted,
    error: ''
  };
};

/**
 * Validate structured address field
 * Returns { isValid: boolean, error: string }
 */
export const validateAddressField = (field, value) => {
  if (!value) {
    return { isValid: true, error: '' }; // Optional
  }

  const sanitized = sanitizeText(value);

  // Required fields: street, city
  if ((field === 'street' || field === 'city') && sanitized.length < 2) {
    return {
      isValid: false,
      error: `${field.charAt(0).toUpperCase() + field.slice(1)} is too short`
    };
  }

  // Postal code validation (basic)
  if (field === 'postalCode' && sanitized.length > 0) {
    if (sanitized.length < 3 || sanitized.length > 12) {
      return {
        isValid: false,
        error: 'Invalid postal code format'
      };
    }
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Format structured address to display string
 */
export const formatStructuredAddress = (address) => {
  if (!address || typeof address !== 'object') return '';

  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);

  // Format state and postal code together
  const stateZip = [address.state, address.postalCode].filter(Boolean).join(' ');
  if (stateZip) parts.push(stateZip);

  if (address.country) parts.push(address.country);

  return parts.join(', ');
};

/**
 * Validate email address
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: true, error: '' }; // Optional field
  }

  const sanitized = sanitizeText(email);

  // Basic email regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(sanitized)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

/**
 * Validate URL (for social media links)
 */
export const validateURL = (url) => {
  if (!url) {
    return { isValid: true, error: '' }; // Optional field
  }

  const sanitized = sanitizeText(url);

  try {
    const urlObj = new URL(sanitized);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return {
        isValid: false,
        error: 'URL must start with http:// or https://'
      };
    }

    return {
      isValid: true,
      error: ''
    };
  } catch (e) {
    return {
      isValid: false,
      error: 'Please enter a valid URL'
    };
  }
};

/**
 * Validate text length
 */
export const validateTextLength = (text, minLength, maxLength, fieldName = 'Field') => {
  if (!text) {
    return { isValid: true, error: '' };
  }

  const sanitized = sanitizeText(text);

  if (minLength && sanitized.length < minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minLength} characters`
    };
  }

  if (maxLength && sanitized.length > maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${maxLength} characters`
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate business name
 */
export const validateBusinessName = (name) => {
  return validateTextLength(name, 2, 100, 'Business name');
};

/**
 * Validate business description
 */
export const validateBusinessDescription = (description) => {
  return validateTextLength(description, 0, 1000, 'Business description');
};

/**
 * Validate time format (HH:MM)
 */
export const validateTimeFormat = (time) => {
  if (!time) {
    return { isValid: false, error: 'Time is required' };
  }

  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

  if (!timeRegex.test(time)) {
    return {
      isValid: false,
      error: 'Invalid time format (use HH:MM)'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate business hours for a single day
 */
export const validateDayHours = (open, close) => {
  const openValidation = validateTimeFormat(open);
  if (!openValidation.isValid) {
    return { isValid: false, error: `Opening time: ${openValidation.error}` };
  }

  const closeValidation = validateTimeFormat(close);
  if (!closeValidation.isValid) {
    return { isValid: false, error: `Closing time: ${closeValidation.error}` };
  }

  // Check that closing time is after opening time
  const openMinutes = parseInt(open.split(':')[0]) * 60 + parseInt(open.split(':')[1]);
  const closeMinutes = parseInt(close.split(':')[0]) * 60 + parseInt(close.split(':')[1]);

  if (closeMinutes <= openMinutes) {
    return {
      isValid: false,
      error: 'Closing time must be after opening time'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate slot interval
 */
export const validateSlotInterval = (interval) => {
  const num = parseInt(interval);

  if (isNaN(num)) {
    return {
      isValid: false,
      error: 'Slot interval must be a number'
    };
  }

  if (num < 15 || num > 120) {
    return {
      isValid: false,
      error: 'Slot interval must be between 15 and 120 minutes'
    };
  }

  if (num % 15 !== 0) {
    return {
      isValid: false,
      error: 'Slot interval must be a multiple of 15 minutes'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate buffer time
 */
export const validateBufferTime = (bufferTime) => {
  const num = parseInt(bufferTime);

  if (isNaN(num)) {
    return {
      isValid: false,
      error: 'Buffer time must be a number'
    };
  }

  if (num < 0 || num > 60) {
    return {
      isValid: false,
      error: 'Buffer time must be between 0 and 60 minutes'
    };
  }

  if (num % 5 !== 0) {
    return {
      isValid: false,
      error: 'Buffer time must be a multiple of 5 minutes'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validate business profile data
 * Returns { isValid: boolean, errors: object }
 */
export const validateBusinessProfile = (profile) => {
  const errors = {};

  // Validate business name
  if (profile.businessName) {
    const nameValidation = validateBusinessName(profile.businessName);
    if (!nameValidation.isValid) {
      errors.businessName = nameValidation.error;
    }
  }

  // Validate business description
  if (profile.businessDescription) {
    const descValidation = validateBusinessDescription(profile.businessDescription);
    if (!descValidation.isValid) {
      errors.businessDescription = descValidation.error;
    }
  }

  // Validate phone
  if (profile.businessPhone) {
    const phoneValidation = validatePhoneNumber(profile.businessPhone);
    if (!phoneValidation.isValid) {
      errors.businessPhone = phoneValidation.error;
    }
  }

  // Validate address
  if (profile.businessAddress) {
    const addressValidation = validateAddressField('street', profile.businessAddress.street);
    if (!addressValidation.isValid) {
      errors.businessAddress = addressValidation.error;
    }
  }

  // Validate email
  if (profile.businessEmail) {
    const emailValidation = validateEmail(profile.businessEmail);
    if (!emailValidation.isValid) {
      errors.businessEmail = emailValidation.error;
    }
  }

  // Validate social media URLs
  if (profile.socialMedia) {
    Object.entries(profile.socialMedia).forEach(([platform, url]) => {
      if (url) {
        const urlValidation = validateURL(url);
        if (!urlValidation.isValid) {
          errors[`socialMedia.${platform}`] = urlValidation.error;
        }
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
